import { animate, state, style, transition, trigger } from '@angular/animations';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ButtonActivity, DonationItem, ENVIRONMENT, Environment } from '@domains';
import { AppService } from '@donor/app.service';
import { FlowService } from '@donor/screens/flow/donor-flow.service';
import { ItemService } from '@rspl-api';
import { Designable, DesignService, FxLayoutAlignDirective, FxLayoutDirective, FxLayoutGapDirective, ResponsiveService } from '@rspl-ui';
import { debounceTime, distinctUntilChanged, filter, forkJoin, switchMap, take, tap, timer } from 'rxjs';

import { AddCustomItemDialogComponent } from '../add-custom-item-dialog/add-custom-item-dialog.component';
import { ItemPreviewDialogComponent } from '../item-preview-dialog/item-preview-dialog.component';

@Component({
  selector: 'app-item-search-dialog',
  templateUrl: './item-search-dialog.component.html',
  styleUrls: ['./item-search-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    FxLayoutDirective,
    FxLayoutGapDirective,
    MatIcon,
    FxLayoutAlignDirective,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatProgressSpinner,
  ],
  animations: [
    trigger('contentHeight', [
      state('loading', style({ height: '160px' })),
      state('results', style({ height: '*' })),
      transition('loading <=> results', animate('300ms ease-in-out')),
    ]),
  ],
})
export class ItemSearchDialogComponent extends Designable implements OnInit {
  contentState: 'loading' | 'results' = 'loading';
  searchControl = new FormControl('');
  isLoading = false;
  result: {
    query: string;
    small: boolean;
    items: DonationItem[];
    small_items: DonationItem[];
    embedding: {
      split_chance: number;
      column_name: string;
      model_name: string;
      name_weight: number;
      description_weight: number;
      min_distance: number;
    };
    small_calculation: {
      max_small_distance: number;
      mean_items_distance: number;
    };
    x_request_id: string;
  };

  public constructor(
    public reference: MatDialogRef<ItemSearchDialogComponent>,
    @Inject(ENVIRONMENT) protected environment: Environment,
    protected override designService: DesignService,
    public override responsiveService: ResponsiveService,
    private itemService: ItemService,
    private flowService: FlowService,
    private appService: AppService,
    protected dialog: MatDialog,
  ) {
    super(designService, responsiveService);
  }

  override ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap((value: string) => {
          if (!value || value.trim() === '') {
            this.isLoading = false;
            this.resetResult();
          }
        }),
        filter((value: string) => value.length >= 2),
        tap(() => {
          this.isLoading = true;
          this.contentState = 'loading';
        }),
        switchMap((term: string) => forkJoin([this.itemService.search(term, this.flowService.lead), timer(500)])),
      )
      .subscribe({
        next: ([data]) => {
          this.result = data;
          this.isLoading = false;
          this.contentState = 'results';
        },
        error: (err) => {
          console.error('Search error', err);
          this.isLoading = false;
          this.contentState = 'results';
        },
      });
  }

  private resetResult(): void {
    this.result = {
      query: '',
      small: false,
      items: [],
      small_items: [],
      embedding: {
        split_chance: 0,
        column_name: '',
        model_name: '',
        name_weight: 0,
        description_weight: 0,
        min_distance: 0,
      },
      small_calculation: {
        max_small_distance: 0,
        mean_items_distance: 0,
      },
      x_request_id: '',
    };
  }

  previewItem(item: DonationItem) {
    this.dialog
      .open(ItemPreviewDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        panelClass: 'item-preview-dialog',
        data: { item: item, request_id: this.result.x_request_id },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.reference.close(true);
        }
      });
    this.appService
      .createLeadActivity(this.flowService.lead?.id, ButtonActivity.ITEM_PREVIEW, item.id, this.result.x_request_id)
      .pipe(take(1))
      .subscribe();
  }

  addCustomItem() {
    this.appService.createLeadActivity(this.flowService.lead?.id, ButtonActivity.CUSTOM_ITEM_CREATE, undefined).pipe(take(1)).subscribe();
    this.dialog
      .open(AddCustomItemDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        panelClass: 'item-preview-dialog',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.reference.close(true);
        }
      });
  }
}
